"use client";

import { Slot } from "@radix-ui/react-slot";
import { type VariantProps } from "class-variance-authority";
import { Children, type HTMLAttributes, isValidElement, type Ref, useMemo } from "react";

import { cn } from "../../utils";
import { BadgeRootContext } from "./context";
import { Icon } from "./Icon";
import { rootStyles } from "./style";

export interface BadgeRootProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof rootStyles> {
  asChild?: boolean;
  ref?: Ref<HTMLDivElement>;
}

export const Root = ({ ref, className, size, shape, variant, style, asChild, ...props }: BadgeRootProps) => {
  const providerValue = useMemo(() => {
    return {
      size,
      shape,
      variant,
    };
  }, [size, shape, variant]);

  const isOnlyIcon = useMemo(() => {
    const childrenArray = Children.toArray(props.children);
    if (childrenArray.length !== 1) {
      return false;
    }

    const element = childrenArray[0];
    return isValidElement(element) && element.type === Icon;
  }, [props.children]);

  const Comp = asChild ? Slot : "div";

  return (
    <BadgeRootContext value={providerValue}>
      <Comp
        ref={ref}
        style={style}
        className={cn(
          rootStyles({ variant, size, shape }),
          {
            // Only icon
            "h-5 min-h-5 w-7 min-w-7": isOnlyIcon && size === "sm",
            "h-6 min-h-6 w-8 min-w-8": isOnlyIcon && size === "md",
            "h-7 min-h-7 w-9 min-w-9": isOnlyIcon && size === "lg",
          },
          className
        )}
        {...props}
      />
    </BadgeRootContext>
  );
};

Root.displayName = "BadgeRoot";
